import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/**
 * Single source of truth for the TV-mode page zoom. Both AppComponent
 * (which renders the +/- buttons and shows the current percentage) and
 * the factory-tv / factory-display pages (which trigger auto-fit after
 * data loads) talk to this service. The actual CSS application is done
 * here once, so we never end up with the UI state and the document state
 * disagreeing.
 *
 * We apply via `document.body.style.setProperty('zoom', ...)` because
 * `zoom` is a non-standard CSS property (Chromium-only, which is fine —
 * Android WebView is Chromium). `transform: scale` would also work but
 * doesn't change the layout box, so the page would either overflow or
 * leave whitespace; `zoom` actually reflows.
 */
@Injectable({ providedIn: 'root' })
export class TvZoomService {
  private readonly STORAGE_KEY = 'tvZoomLevel';
  private readonly MIN = 0.2;
  private readonly MAX = 2.0;
  private readonly STEP = 0.1;

  private readonly zoom$ = new BehaviorSubject<number>(this.loadPersisted());
  /** Observable so AppComponent's button bar stays in sync when factory
   *  views auto-fit. */
  readonly zoom = this.zoom$.asObservable();

  constructor() {
    this.apply(this.zoom$.value);
  }

  get current(): number { return this.zoom$.value; }

  zoomIn(): void { this.set(this.zoom$.value + this.STEP); }
  zoomOut(): void { this.set(this.zoom$.value - this.STEP); }
  reset(): void { this.set(1.0); }

  set(z: number): void {
    const clamped = Math.max(this.MIN, Math.min(this.MAX, z));
    const rounded = Math.round(clamped * 100) / 100;
    if (rounded === this.zoom$.value) return;
    this.zoom$.next(rounded);
    localStorage.setItem(this.STORAGE_KEY, String(rounded));
    this.apply(rounded);
  }

  /**
   * Measure a "content" element against the available viewport and zoom out
   * (never in) to make it fit. Called from factory-tv / factory-display
   * after each data refresh. headerEl is excluded from the available
   * height so the header stays full-size.
   */
  autoFitToViewport(contentEl: HTMLElement, headerEl?: HTMLElement | null): void {
    if (!contentEl) return;
    const viewportH = window.innerHeight;
    const viewportW = window.innerWidth;
    const headerH = headerEl ? headerEl.getBoundingClientRect().height : 0;
    // Reset to 1.0 before measuring so scrollHeight reflects natural size,
    // otherwise an existing zoom would distort the reading.
    this.apply(1);
    const contentH = contentEl.scrollHeight;
    const contentW = contentEl.scrollWidth;
    this.apply(this.zoom$.value); // restore until we compute the new one

    const availableH = Math.max(1, viewportH - headerH);
    const fitH = availableH / Math.max(1, contentH);
    const fitW = viewportW / Math.max(1, contentW);
    let fit = Math.min(fitH, fitW, 1.0);
    // Floor at 0.5 — under that the cards become unreadable on a TV. If
    // the content truly needs less than 50% it means the card design needs
    // tightening, not the zoom. Better to show a clipped tail than ant-sized
    // text, until we redesign the card.
    fit = Math.max(0.5, fit);
    // Small safety margin so the last row isn't kissing the bottom edge.
    this.set(fit * 0.97);
  }

  private apply(z: number): void {
    document.body.style.setProperty('zoom', String(z));
  }

  private loadPersisted(): number {
    const stored = parseFloat(localStorage.getItem(this.STORAGE_KEY) || '');
    if (isNaN(stored)) return 1.0;
    return Math.max(this.MIN, Math.min(this.MAX, stored));
  }
}
